/**
 * api/sites.js — sites, bays, pallets, picklists, site-rules,
 * public-link + public-info read endpoints.
 */
import { supabase } from "@/lib/supabaseClient";
import { nullEmpty } from "@/lib/constants";
import { getClientSlug } from "./_shared";

/* =========================  SITES  ========================= */

export async function listSites({ search = "", activeOnly = false, clientId = null } = {}) {
  let q = supabase
    .from("lbs_sites")
    .select(
      "id,name,code,city,state,timezone,operating_hours_start,operating_hours_end,slot_duration_minutes,is_active,client_id,created_at"
    )
    .order("name", { ascending: true });

  if (search) {
    q = q.or(`name.ilike.%${search}%,code.ilike.%${search}%,city.ilike.%${search}%`);
  }
  if (activeOnly) q = q.eq("is_active", true);
  if (clientId)   q = q.eq("client_id", clientId);
  return q;
}

export async function getSite(id) {
  return supabase.from("lbs_sites").select("*").eq("id", id).maybeSingle();
}

export async function createSite(values) {
  const payload = nullEmpty(values);
  return supabase.from("lbs_sites").insert(payload).select().maybeSingle();
}

export async function updateSite(id, values) {
  const payload = nullEmpty(values);
  // Strip system / read-only columns so they never go on the wire.
  delete payload.id;
  delete payload.client_id;
  delete payload.created_at;
  delete payload.updated_at;
  // .select() forces Prefer: return=representation so the server replies
  // with a 200 + body (avoids the 204-no-body fetch error). maybeSingle is
  // tolerant of RLS returning 0 rows; the parent calls fetchAll() afterwards
  // anyway, so we don't depend on the body here.
  return supabase
    .from("lbs_sites")
    .update(payload)
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function deleteSite(id) {
  return supabase.from("lbs_sites").delete().eq("id", id).select();
}

export async function siteBayCount(siteId) {
  const { count } = await supabase
    .from("lbs_bays")
    .select("id", { count: "exact", head: true })
    .eq("site_id", siteId);
  return count ?? 0;
}

export async function listSitesLite(clientId = null) {
  let q = supabase
    .from("lbs_sites")
    .select("id,name,code,timezone,state,is_active,client_id,operating_hours_start,operating_hours_end,slot_duration_minutes")
    .eq("is_active", true)
    .order("name", { ascending: true });
  if (clientId) q = q.eq("client_id", clientId);
  return q;
}

/**
 * PR-1 (buffer): fetch per-site buffer default without breaking pre-migration
 * DBs (where the column doesn't exist yet). Returns 0 on any failure.
 */
export async function getSiteBufferDefault(siteId) {
  if (!siteId) return 0;
  const { data, error } = await supabase
    .from("lbs_sites")
    .select("default_buffer_minutes")
    .eq("id", siteId)
    .maybeSingle();
  if (error) return 0; // 42703 undefined_column before migration 150
  return parseInt(data?.default_buffer_minutes, 10) || 0;
}

export async function listSitesPublic() {
  return supabase.from("lbs_sites").select("id,name,code,city,state").eq("is_active", true).order("name");
}

/* =========================  BAYS  ========================== */

// Natural-order collator — sorts "Bay 1, Bay 2, Bay 10" correctly
// instead of lex-order ("Bay 1, Bay 10, Bay 2").
//
// 2026-02 update: previously we sorted by `sort_order` first and used
// natural-name as a tiebreaker. But BayForm auto-assigns
// sort_order = max + 1 on create, so bays created in the order
// "Bay 1, Bay 10, Bay 11, Bay 2, Bay 20, Bay 3" ended up displayed
// in that exact (wrong) sequence. We now use natural-name as the
// primary key — which matches user expectation for numerically-named
// bays — and fall back to sort_order as a tiebreaker for admins who
// deliberately want a manual override on same-named rows.
const NATURAL = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
function sortBaysNaturally(rows) {
  if (!Array.isArray(rows)) return rows;
  return [...rows].sort((a, b) => {
    // Primary: numeric-aware name compare (handles "Bay 2" < "Bay 10").
    const byName = NATURAL.compare(a?.name || "", b?.name || "");
    if (byName !== 0) return byName;
    // Tiebreaker: sort_order for admins who set it explicitly.
    const ao = a?.sort_order ?? Number.POSITIVE_INFINITY;
    const bo = b?.sort_order ?? Number.POSITIVE_INFINITY;
    return ao - bo;
  });
}

export async function listBays(siteId) {
  const resp = await supabase
    .from("lbs_bays")
    .select("*")
    .eq("site_id", siteId);
  if (resp.data) resp.data = sortBaysNaturally(resp.data);
  return resp;
}

export async function createBay(siteId, values) {
  const payload = { ...nullEmpty(values), site_id: siteId };
  return supabase.from("lbs_bays").insert(payload).select().maybeSingle();
}

export async function updateBay(id, values) {
  const payload = nullEmpty(values);
  delete payload.id;
  delete payload.site_id;
  delete payload.created_at;
  delete payload.updated_at;
  return supabase
    .from("lbs_bays")
    .update(payload)
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function deleteBay(id) {
  return supabase.from("lbs_bays").delete().eq("id", id).select();
}

export async function bulkSetBayActive(ids, isActive) {
  return supabase.from("lbs_bays").update({ is_active: isActive }).in("id", ids).select();
}

export async function bulkDeleteBays(ids) {
  return supabase.from("lbs_bays").delete().in("id", ids).select();
}

/**
 * Re-sequence bays at a site so `sort_order` matches natural-name order
 * (numeric-aware). Called from the "Re-sequence bays" button on the site
 * detail page — one-click fix for tenants whose bays were auto-numbered
 * in creation order rather than name order.
 *
 * Returns { count, error } where count is the number of bays touched.
 * Skips no-op rows (already in the right position) to keep the update
 * count honest.
 */
export async function resequenceBays(siteId) {
  const { data, error } = await supabase
    .from("lbs_bays")
    .select("id,name,sort_order")
    .eq("site_id", siteId);
  if (error) return { count: 0, error };
  const sorted = [...(data || [])].sort((a, b) =>
    NATURAL.compare(a?.name || "", b?.name || "")
  );
  const updates = sorted
    .map((row, idx) => ({ id: row.id, next: idx + 1, current: row.sort_order }))
    .filter((u) => u.current !== u.next);
  if (updates.length === 0) return { count: 0, error: null };
  const results = await Promise.all(
    updates.map((u) =>
      supabase.from("lbs_bays").update({ sort_order: u.next }).eq("id", u.id).select()
    )
  );
  const firstErr = results.find((r) => r.error)?.error || null;
  return { count: updates.length, error: firstErr };
}

/**
 * Lightweight bays for a site (picker dropdowns, dock grid, etc.).
 * @param {string} siteId
 * @param {object} [opts]
 * @param {boolean} [opts.includeInactive=false] — when true, returns
 *   inactive bays as well. Admin/operator UIs pass true so they can still
 *   discover decommissioned bays; booker / public flows leave it false.
 */
export async function listBaysLite(siteId, { includeInactive = false } = {}) {
  let q = supabase
    .from("lbs_bays")
    .select("id,name,code,color,is_active,is_on_demand,requires_approval,max_booking_hours,slot_duration_minutes,sort_order,site_id,booking_note")
    .eq("site_id", siteId);
  if (!includeInactive) q = q.eq("is_active", true);
  const resp = await q;
  if (resp.data) resp.data = sortBaysNaturally(resp.data);
  return resp;
}

/**
 * PR-1 (buffer): fetch per-bay buffer_minutes separately so the main lister
 * works on pre-migration-150 DBs. Returns a Map(bayId → buffer | null).
 * Silently returns an empty map if the column doesn't exist yet.
 */
export async function listBayBuffers(siteId) {
  if (!siteId) return new Map();
  const { data, error } = await supabase
    .from("lbs_bays")
    .select("id,buffer_minutes")
    .eq("site_id", siteId);
  if (error) return new Map();
  const out = new Map();
  (data || []).forEach((r) => out.set(r.id, r.buffer_minutes ?? null));
  return out;
}

/* =========================  PALLETS  ======================= */

export async function listPalletBalance({ clientId = null, brand = null } = {}) {
  let q = supabase
    .from("lbs_v_pallet_balance")
    .select("*")
    .order("net_balance", { ascending: false })
    .limit(500);
  if (clientId) q = q.eq("client_id", clientId);
  if (brand)    q = q.eq("brand", brand);
  return q;
}

export async function listPalletMovements({ clientId = null, brand = null, fromDate = null } = {}) {
  let q = supabase
    .from("lbs_v_pallet_movements")
    .select("*")
    .order("movement_date", { ascending: false })
    .limit(500);
  if (clientId) q = q.eq("client_id", clientId);
  if (brand)    q = q.eq("brand", brand);
  if (fromDate) q = q.gte("movement_date", fromDate);
  return q;
}

/* =========================  SITE RULES  ==================== */

export async function getSiteRules(siteId) {
  return supabase.from("lbs_site_rules").select("*").eq("site_id", siteId).maybeSingle();
}

export async function upsertSiteRules(siteId, values) {
  return supabase
    .from("lbs_site_rules")
    .upsert({ site_id: siteId, ...values, updated_at: new Date().toISOString() }, { onConflict: "site_id" })
    .select()
    .maybeSingle();
}

export async function uploadSiteRuleFile(siteId, file) {
  const { data: site } = await supabase
    .from("lbs_sites")
    .select("client_id")
    .eq("id", siteId)
    .maybeSingle();
  if (!site?.client_id) return { error: { message: "Site has no client" } };
  const slug = await getClientSlug(site.client_id);
  if (!slug) return { error: { message: "Client bucket not provisioned." } };
  const bucket = `lbs-${slug}`;
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
  const path = `site-rules/${siteId}/${Date.now()}-${safeName}`;

  // Read the File into an ArrayBuffer FIRST. This sidesteps a known
  // @supabase/supabase-js storage bug ("Response.arrayBuffer: Body
  // has already been consumed") triggered when an upload error path
  // tries to read the same Response body twice. Uploading an in-memory
  // buffer with an explicit contentType avoids the double-read.
  let buffer;
  try {
    buffer = await file.arrayBuffer();
  } catch (e) {
    return { error: { message: e?.message || "Could not read file" } };
  }

  try {
    const up = await supabase.storage.from(bucket).upload(path, buffer, {
      contentType: file.type || "application/octet-stream",
      upsert: true,
    });
    if (up.error) return { error: { message: up.error.message || "Upload failed" } };
  } catch (e) {
    return { error: { message: e?.message || "Upload failed" } };
  }
  const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
  return { data: { url: pub.publicUrl, name: file.name, path, bucket } };
}

export async function listSiteRuleSections(siteId) {
  return supabase
    .from("lbs_site_rule_sections")
    .select("*")
    .eq("site_id", siteId)
    .order("display_order");
}

export async function upsertSiteRuleSection(row) {
  if (row.id) {
    return supabase.from("lbs_site_rule_sections").update(row).eq("id", row.id).select().maybeSingle();
  }
  return supabase.from("lbs_site_rule_sections").insert(row).select().maybeSingle();
}

export async function deleteSiteRuleSection(id) {
  return supabase.from("lbs_site_rule_sections").delete().eq("id", id).select();
}

export async function getPublicSiteRules(siteId) {
  const [r, s] = await Promise.all([
    supabase.from("lbs_site_rules").select("*").eq("site_id", siteId).maybeSingle(),
    supabase.from("lbs_site_rule_sections")
      .select("*")
      .eq("site_id", siteId)
      .eq("is_internal", false)
      .order("display_order"),
  ]);
  return { rules: r.data, sections: s.data || [], error: r.error || s.error };
}

export async function getPublicSiteInfo(clientSlug, siteId) {
  const [r, s, site] = await Promise.all([
    supabase.from("lbs_site_rules").select("*").eq("site_id", siteId).maybeSingle(),
    supabase.from("lbs_site_rule_sections")
      .select("*")
      .eq("site_id", siteId)
      .eq("is_internal", false)
      .order("display_order"),
    supabase
      .from("lbs_sites")
      .select("id,name,city,state,timezone,operating_hours_start,operating_hours_end,contact_email,contact_phone,client_id")
      .eq("id", siteId).maybeSingle(),
  ]);
  return { rules: r.data, sections: s.data || [], site: site.data || null };
}

/** Everything the availability engine needs for one site (full site + bay rows). */
export async function getAvailabilityInputs(siteId, { includeInactive = false } = {}) {
  const [site, bays] = await Promise.all([
    supabase.from("lbs_sites").select("*").eq("id", siteId).single(),
    (() => {
      let q = supabase.from("lbs_bays").select("*").eq("site_id", siteId)
        .order("sort_order", { ascending: true, nullsFirst: false }).order("name");
      if (!includeInactive) q = q.eq("is_active", true);
      return q;
    })(),
  ]);
  const client = site.data?.client_id
    ? await supabase.from("lbs_clients").select("*").eq("id", site.data.client_id).single()
    : { data: null };
  const autoAssign = site.data?.bay_auto_assign ?? client.data?.bay_auto_assign ?? false;
  return { site: site.data, bays: bays.data || [], bufferEnabled: !!client.data?.buffer_time_enabled, autoAssign };
}
